import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Input,
  VStack,
  HStack,
  Text,
  Spinner,
  Flex,
  CloseButton,
} from '@chakra-ui/react';
import { ChatIcon } from '@chakra-ui/icons';
import { fetchAuthSession } from 'aws-amplify/auth';

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setLoading(true);

    try {
      // Get auth token if user is logged in
      let headers = { 'Content-Type': 'application/json' };
      try {
        const session = await fetchAuthSession();
        const token = session?.tokens?.idToken?.toString();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch {
        // Not logged in — continue without auth
      }

      const res = await fetch('/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });
      const data = await res.json();

      if (data.session_id) setSessionId(data.session_id);

      const reply = data.response || data.error || 'Sorry, something went wrong.';
      setMessages((prev) => [...prev, { role: 'assistant', text: reply }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', text: 'Unable to reach the chatbot. Please try again later.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <IconButton
        aria-label="Open Bullseye chat assistant"
        icon={<Text fontSize="24px">🎯</Text>}
        onClick={() => setIsOpen(true)}
        position="fixed"
        bottom="24px"
        right="24px"
        zIndex="1000"
        colorScheme="orange"
        size="lg"
        borderRadius="full"
        boxShadow="lg"
      />
    );
  }

  return (
    <Box
      position="fixed"
      bottom="24px"
      right={{ base: '0', md: '24px' }}
      width={{ base: '100%', md: '380px' }}
      maxH="480px"
      bg="white"
      borderRadius="12px"
      boxShadow="dark-lg"
      display="flex"
      flexDirection="column"
      zIndex="1000"
    >
      {/* Header */}
      <Flex
        bg="#FD5521"
        color="white"
        px={4}
        py={2}
        borderTopRadius="12px"
        align="center"
        justify="space-between"
      >
        <Text fontWeight="600" fontSize="sm">Bullseye — Club Assistant</Text>
        <CloseButton size="sm" color="white" onClick={() => {
          setIsOpen(false);
          setMessages([]);
          setSessionId(null);
        }} />
      </Flex>

      {/* Messages */}
      <VStack
        flex="1"
        overflowY="auto"
        px={3}
        py={2}
        spacing={2}
        align="stretch"
        maxH="340px"
      >
        {messages.length === 0 && (
          <Text fontSize="xs" color="gray.500" textAlign="center" py={3}>
            Ask me about the club — hours, membership, matches, facilities, or weather!
          </Text>
        )}
        {messages.map((msg, i) => (
          <Box
            key={i}
            alignSelf={msg.role === 'user' ? 'flex-end' : 'flex-start'}
            bg={msg.role === 'user' ? '#FD5521' : 'gray.100'}
            color={msg.role === 'user' ? 'white' : 'black'}
            px={3}
            py={2}
            borderRadius="lg"
            maxW="85%"
            fontSize="sm"
            whiteSpace="pre-wrap"
          >
            {msg.text}
          </Box>
        ))}
        {loading && (
          <HStack alignSelf="flex-start">
            <Spinner size="sm" color="orange.500" />
            <Text fontSize="xs" color="gray.500">Thinking...</Text>
          </HStack>
        )}
        <div ref={messagesEndRef} />
      </VStack>

      {/* Input */}
      <HStack px={3} py={2} borderTop="1px solid" borderColor="gray.200">
        <Input
          placeholder="Type a message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          size="sm"
          borderRadius="full"
          isDisabled={loading}
        />
        <IconButton
          aria-label="Send message"
          icon={<ChatIcon />}
          onClick={sendMessage}
          size="sm"
          colorScheme="orange"
          borderRadius="full"
          isLoading={loading}
        />
      </HStack>
    </Box>
  );
};
